"use client";

/**
 * Affordability profile: "can I afford this?"
 *
 * Four numbers the person knows by heart (net monthly income, a
 * co-borrower's income, existing monthly loan payments, savings for the
 * down payment) plus the mortgage track they expect to use. From those
 * one derived number, the highest price they can carry, drives the
 * chips on listing cards, the "Büdcəmə uyğun" quick pick and the
 * calculators.
 *
 * Storage is device-first: the profile lives in localStorage so a
 * signed-out visitor gets the chips too. A signed-in session syncs it
 * to the account (PATCH /auth/me/finance) so other devices see it; the
 * server copy wins on first load when the device has none.
 *
 * Rules (from the eligibility checker, state mortgage programme):
 *   preferential 4%, up to 100 000 AZN, up to 30 years, 10% down
 *   ordinary     8%, up to 150 000 AZN, up to 25 years, 15% down
 *   both: payment at most 70% of income, at least 317 AZN left after it
 *   bank: no programme cap; 11% and 20 years are the mortgage
 *   calculator's defaults, payment at most 45% of income (a common bank
 *   rule of thumb, shown as an assumption, adjustable per bank).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

export interface FinanceProfile {
  income: number | null;
  /** A second earner buying together. */
  coIncome: number | null;
  /** What the household spends in a normal month, rent aside. */
  expenses: number | null;
  /** What is already put aside for the purchase. */
  savings: number | null;
  /** Buying outright, no loan: the budget is simply the savings. */
  cash: boolean;
  /** Loan terms the buyer expects. The programme picker is gone: people
   *  know their own rate (owner, 2026-09-21). */
  rate: number | null;
  years: number | null;
  /** The down payment the lender demands, as a share of the price. */
  downShare: number | null;
  updatedAt: number;
}

/** Defaults: the ordinary state mortgage, which is what most buyers in
 *  Baku are quoted. Every one of them is an input on the form. */
export const DEFAULTS = { rate: 8, years: 25, downShare: 15, paymentShare: 0.7 } as const;



/** Living minimum for 2026 (AZN/month) that must remain after the payment. */
export const LIVING_MINIMUM = 317;

/**
 * What the state actually lends, from the Mortgage and Credit Guarantee
 * Fund's own terms (mcgf.gov.az, checked 2026-09-21): the preferential
 * mortgage at 4% tops out at 100 000 AZN over 30 years with 10% down, the
 * ordinary one at 8% at 150 000 AZN over 25 years with 15% down. A bank's
 * own mortgage costs 12% to 16% and has no published ceiling, so above
 * that rate no cap is applied (owner, 2026-09-21: "there is a maximum of
 * how much you can actually get a mortgage for, so we cannot have it
 * ourselves").
 */
export const STATE_LOAN_CAP = { preferential: 100_000, ordinary: 150_000 } as const;

/**
 * What banks write on their own books, checked 2026-09-21: Xalq Bank
 * caps its internal mortgage at 250 000 AZN (from 11%, 22 years, 30%
 * down), while ABB advertises up to 500 000 AZN over 25 years. So 250 000
 * is the ordinary bank ceiling and 500 000 the highest offer on the
 * market; nothing above that is advertised anywhere (owner, 2026-09-21:
 * "does anybody give 450k loan?").
 */
export const BANK_LOAN_CAP = { typical: 250_000, highest: 500_000 } as const;

/** The state programme's ceiling at this rate, or null above 8%. */
export function stateLoanCap(ratePct: number): number | null {
  if (ratePct <= 4) return STATE_LOAN_CAP.preferential;
  if (ratePct <= 8) return STATE_LOAN_CAP.ordinary;
  return null;
}

/** The largest loan anyone writes at this rate, and which kind it is. */
export function lenderCeiling(ratePct: number): { cap: number; kind: "state" | "bank" } {
  const state = stateLoanCap(ratePct);
  return state != null ? { cap: state, kind: "state" } : { cap: BANK_LOAN_CAP.highest, kind: "bank" };
}

const KEY = "menzil.affordability.v1";
/** Trailing window for the account sync. */
const PUSH_DEBOUNCE_MS = 800;
const EMPTY: FinanceProfile = {
  income: null, coIncome: null, expenses: null, savings: null,
  cash: false, rate: null, years: null, downShare: null, updatedAt: 0,
};

/** The loan terms in force: the buyer's own numbers, else the defaults. */
export function termsFor(p: FinanceProfile): { rate: number; years: number; downShare: number } {
  return {
    rate: p.rate != null && p.rate >= 0 ? p.rate : DEFAULTS.rate,
    years: p.years != null && p.years > 0 ? Math.min(30, Math.round(p.years)) : DEFAULTS.years,
    downShare: p.downShare != null && p.downShare >= 0 ? Math.min(100, p.downShare) : DEFAULTS.downShare,
  };
}

type Listener = () => void;
const store = {
  profile: null as FinanceProfile | null,
  loaded: false,
  syncedFor: null as string | null,
  listeners: new Set<Listener>(),
  notify() { this.listeners.forEach((l) => l()); },
};

/** The device copy, read synchronously (for prefilling other
 *  calculators on mount). Null when nothing was entered yet. */
export function readAffordabilityProfile(): FinanceProfile | null {
  if (typeof window === "undefined") return null;
  load();
  return store.profile;
}

function readLocal(): FinanceProfile | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<FinanceProfile>;
    // Older copies carried a programme track and two fields that are gone;
    // keep what still applies and let the rest fall back to the defaults.
    return { ...EMPTY, ...p, cash: p.cash === true };
  } catch {
    return null;
  }
}

function writeLocal(p: FinanceProfile | null) {
  try {
    if (p) localStorage.setItem(KEY, JSON.stringify(p));
    else localStorage.removeItem(KEY);
  } catch {
    /* private mode: the profile lives for this page only */
  }
}

/** Reads the device copy once; true on the call that did the read. */
function load(): boolean {
  if (store.loaded) return false;
  store.loaded = true;
  store.profile = readLocal();
  return true;
}

/** Has enough to compute anything: an income, or savings when buying
 *  outright, where income plays no part. */
export function isProfileUsable(p: FinanceProfile | null): p is FinanceProfile {
  if (!p) return false;
  if (p.cash) return (p.savings ?? 0) > 0;
  return (p.income ?? 0) + (p.coIncome ?? 0) > 0;
}

export function annuity(principal: number, annualRatePct: number, years: number): number {
  const n = Math.max(1, Math.round(years * 12));
  const i = annualRatePct / 100 / 12;
  if (principal <= 0) return 0;
  if (i <= 0) return principal / n;
  return (principal * i) / (1 - Math.pow(1 + i, -n));
}

function principalFor(payment: number, annualRatePct: number, years: number): number {
  const n = Math.max(1, Math.round(years * 12));
  const i = annualRatePct / 100 / 12;
  if (payment <= 0) return 0;
  if (i <= 0) return payment * n;
  return (payment * (1 - Math.pow(1 + i, -n))) / i;
}

export interface Budget {
  rate: number;
  years: number;
  /** Highest monthly payment the rules allow. */
  maxPayment: number;
  /** Highest loan the payment carries, after the programme cap. */
  maxLoan: number;
  /** Highest price: loan plus savings, also bounded by the down-payment share. */
  maxPrice: number;
  /** Which bound decided maxPrice. ``no_savings`` means the figure is the
   *  loan alone: nothing is put aside yet, so no price can be named. */
  boundBy: "payment" | "down_payment" | "savings" | "no_savings" | "loan_cap";
}

export function computeBudget(p: FinanceProfile): Budget | null {
  if (!isProfileUsable(p)) return null;
  const savings = p.savings ?? 0;
  const { rate, years, downShare } = termsFor(p);
  if (p.cash) {
    return { rate: 0, years: 0, maxPayment: 0, maxLoan: 0, maxPrice: Math.floor(savings), boundBy: "savings" };
  }
  const income = (p.income ?? 0) + (p.coIncome ?? 0);
  const expenses = p.expenses ?? 0;
  // Two ceilings, whichever binds first: the customary share of income,
  // and what is actually left after the household's own outgoings, with
  // the living minimum kept back.
  const maxPayment = Math.max(0, Math.min(income * DEFAULTS.paymentShare, income - LIVING_MINIMUM - expenses));
  // A payment you could carry does not mean a loan anyone will write: at
  // a state rate the programme's own ceiling binds first.
  const byPayment = principalFor(maxPayment, rate, years);
  const { cap } = lenderCeiling(rate);
  const cappedByState = cap < byPayment;
  const maxLoan = cappedByState ? cap : byPayment;
  // Nothing saved yet: a price cannot be named, since every lender wants a
  // down payment. Report the loan itself and say so (owner, 2026-09-21:
  // "I say I have a 100 000 salary and it shows 0").
  if (savings <= 0) {
    return { rate, years, maxPayment, maxLoan, maxPrice: Math.floor(maxLoan), boundBy: "no_savings" };
  }
  let maxPrice = maxLoan + savings;
  let boundBy: Budget["boundBy"] = cappedByState ? "loan_cap" : "payment";
  const byDown = downShare > 0 ? savings / (downShare / 100) : Infinity;
  if (byDown < maxPrice) {
    maxPrice = byDown;
    boundBy = "down_payment";
  }
  return { rate, years, maxPayment, maxLoan, maxPrice: Math.max(0, Math.floor(maxPrice)), boundBy };
}

/** What the household can put aside each month: income minus what it
 *  spends. Asking for it as a separate field was one field too many
 *  (owner, 2026-09-21). */
export function monthlySavingOf(p: FinanceProfile): number {
  return Math.max(0, (p.income ?? 0) + (p.coIncome ?? 0) - (p.expenses ?? 0));
}

export interface TimeToAfford {
  /** Months until the price is within reach with a mortgage; 0 when it
   *  already is, null when it cannot be answered (no monthly saving). */
  withLoan: number | null;
  /** Months until the whole price is saved, buying outright. */
  withoutLoan: number | null;
}

/** How long until ``target`` is affordable, saving ``monthlySaving`` a
 *  month. With a loan the gap to close is the down payment the programme
 *  and the loan ceiling demand; without one it is the whole price. */
export function timeToAfford(p: FinanceProfile, target: number): TimeToAfford {
  const b = computeBudget(p);
  const savings = p.savings ?? 0;
  const perMonth = monthlySavingOf(p);
  const months = (deficit: number): number | null => {
    if (deficit <= 0) return 0;
    if (perMonth <= 0) return null;
    return Math.ceil(deficit / perMonth);
  };
  const { downShare } = termsFor(p);
  const neededDown = b
    ? Math.max((target * downShare) / 100, target - b.maxLoan)
    : target;
  return {
    withLoan: b ? months(neededDown - savings) : null,
    withoutLoan: months(target - savings),
  };
}

export interface Fit {
  fits: boolean;
  /** Monthly payment at this price with the person's savings as down payment. */
  monthly: number;
  /** Share of income the payment takes. */
  incomeShare: number;
  /** How far over the budget the price is (0 when it fits). */
  overBy: number;
  /** Extra down payment that would make it fit, when the down share is the blocker. */
  extraDown: number;
}

export function fitFor(p: FinanceProfile, price: number): Fit | null {
  const b = computeBudget(p);
  if (!b || price <= 0) return null;
  const savings = p.savings ?? 0;
  const income = (p.income ?? 0) + (p.coIncome ?? 0);
  const requiredDown = (price * termsFor(p).downShare) / 100;
  const down = Math.min(price, Math.max(savings, 0));
  const loan = Math.max(0, price - down);
  const monthly = annuity(loan, b.rate, b.years);
  const fits = price <= b.maxPrice;
  return {
    fits,
    monthly,
    incomeShare: income > 0 ? monthly / income : 1,
    overBy: Math.max(0, price - b.maxPrice),
    extraDown: Math.max(0, requiredDown - savings),
  };
}

async function pullServer(): Promise<FinanceProfile | null> {
  try {
    const r = await fetch(`${API_BASE}/auth/me/finance`, { credentials: "include" });
    if (!r.ok) return null;
    const d = await r.json();
    const p = d?.profile;
    if (!p || typeof p !== "object") return null;
    return {
      income: typeof p.income === "number" ? p.income : null,
      coIncome: typeof p.co_income === "number" ? p.co_income : null,
      expenses: typeof p.expenses === "number" ? p.expenses : null,
      savings: typeof p.savings === "number" ? p.savings : null,
      cash: p.cash === true,
      rate: typeof p.rate === "number" ? p.rate : null,
      years: typeof p.years === "number" ? p.years : null,
      downShare: typeof p.down_share === "number" ? p.down_share : null,
      updatedAt: Date.now(),
    };
  } catch {
    return null;
  }
}

// Typing and dragging change the profile many times a second; the
// account copy only has to end up right. One trailing write per pause
// (owner, 2026-09-21: a range drag was firing a PATCH per pixel).
let pushTimer: ReturnType<typeof setTimeout> | null = null;
let pushPending: FinanceProfile | null | undefined;

function schedulePush(p: FinanceProfile | null): void {
  pushPending = p;
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    const payload = pushPending;
    pushPending = undefined;
    if (payload !== undefined) void pushServer(payload);
  }, PUSH_DEBOUNCE_MS);
}

/** Flush a pending write immediately (page hide / unload). */
function flushPush(): void {
  if (!pushTimer) return;
  clearTimeout(pushTimer);
  pushTimer = null;
  const payload = pushPending;
  pushPending = undefined;
  if (payload !== undefined) void pushServer(payload);
}

async function pushServer(p: FinanceProfile | null): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/me/finance`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(p ? {
        income: p.income, co_income: p.coIncome, expenses: p.expenses,
        savings: p.savings, cash: p.cash, rate: p.rate, years: p.years,
        down_share: p.downShare,
      } : {}),
    });
  } catch {
    /* the device copy stands; the next save retries */
  }
}

export function useAffordability() {
  const { user } = useAuth();
  const [, bump] = useState(0);

  useEffect(() => {
    const l = () => bump((v) => v + 1);
    store.listeners.add(l);
    // Only the first read changes anything; notifying on every mount
    // re-rendered every mounted consumer once per new one.
    if (load()) store.notify();
    if (user && store.syncedFor !== user.id) {
      store.syncedFor = user.id;
      void pullServer().then((remote) => {
        if (remote && !isProfileUsable(store.profile)) {
          store.profile = remote;
          writeLocal(remote);
          store.notify();
        } else if (!remote && isProfileUsable(store.profile)) {
          void pushServer(store.profile);
        }
      });
    }
    // A pending sync must not be lost when the tab goes away.
    const onHide = () => { if (document.visibilityState === "hidden") flushPush(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", flushPush);
    return () => {
      store.listeners.delete(l);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", flushPush);
    };
  }, [user]);

  const save = useCallback((patch: Partial<FinanceProfile>) => {
    load();
    const next: FinanceProfile = { ...(store.profile ?? EMPTY), ...patch, updatedAt: Date.now() };
    store.profile = next;
    writeLocal(next);
    store.notify();
    if (user) schedulePush(next);
  }, [user]);

  const clear = useCallback(() => {
    store.profile = null;
    writeLocal(null);
    store.notify();
    if (user) schedulePush(null);
  }, [user]);

  const profile = store.profile;
  const budget = useMemo(() => (isProfileUsable(profile) ? computeBudget(profile) : null), [profile]);
  const fit = useCallback(
    (price: number | null | undefined) =>
      price && isProfileUsable(store.profile) ? fitFor(store.profile, price) : null,
    [],
  );
  return { profile, usable: isProfileUsable(profile), budget, fit, save, clear };
}
